/* eslint-disable react/no-unescaped-entities */
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { Trans, useTranslation } from 'react-i18next';

import LoadThemeComponent from '../../base/LoadThemeComponent';
import { Portal } from '../../utils/Portal';
import * as formatters from '../../utils/formatters';
import { isDefault, isTemplateDelta, isThankYouLotto } from '../../utils/template';
import DatePicker from '../Widgets/Views/Components/DatePicker/DatePicker';
import Select, { Option } from '../Widgets/Views/Components/Select';
import DrawAnimation from '../draw-animation';
import Loader from '../loader/Loader';
import { LotteryTypes } from '../play/Types/Enums';

import BlockchainInfo from './BlockchainInfo';
import Jackpot from './Jackpot';
import Prizes from './Prizes';
import RafflePrizes, { PrizeType } from './RafflePrizes';
import Balls, { ensureNumberArray } from './elements/Balls/Balls';
import ResultsDate from './elements/ResultsDate';
import ResultsDropdowns from './elements/ResultsDropdowns';
import { BetsSection } from './elements/Soccer/BetsSection';

import LatestResultsApi from 'Api/SiteApi/LatestResultsApi';
import { BasicDrawRecord, LotteryDrawRecord } from 'Api/SiteApi/Responses';
import { Route } from 'components/common/Route';
import mapLotterySlugToMachine from 'components/draw-animation/services/mapLotterySlugToMachine';
import { findNearestDate, formatNumber } from 'utils/helpers';

type Props = Readonly<{ [key: PropertyKey]: string | null }>;

function ResultsDetails({ lotteryslug, timezone, transparencyurl, drawinganimation }: Props) {
    const isTemplateThankYouLotto = useMemo(() => isThankYouLotto(), []);
    const [allDraws, setAllDraws] = useState<BasicDrawRecord[]>([]);
    const fetchedMonths = useRef<string[]>([]);
    const [lotteryDraw, setLotteryDraw] = useState<LotteryDrawRecord | null>(null);
    const [prizes, setPrizes] = useState<PrizeType[]>([]);
    const [lotteryType, setLotteryType] = useState<LotteryTypes | null>(null);
    const [numberPadding, setNumberPadding] = useState<number>(0);
    const [noDrawsYet, setNoDrawsYet] = useState<boolean>(false);
    const [selectedDrawNo, setSelectedDrawNo] = useState<number>(
        parseInt((/\/(\d+)$/.exec(window.location.pathname) || [])[1]) || 0
    );
    const [dailyDraws, setDailyDraws] = useState<BasicDrawRecord[]>([]);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const { t } = useTranslation();
    const extractDate = (date: string | null) => (date ? moment(date).format('Y-MM-DD') : null);

    const [defaultDate, setDefaultDate] = useState('');
    const [time, setTime] = useState('');
    const drawDates = Array.from(new Set(allDraws.map((item) => extractDate(item.date))));
    const [noDrawsForSelectedDate, setNoDrawsForSelectedDate] = useState(false);

    const getDrawsForCalendar = async (date: Date) => {
        const monthDate = formatDateToYMD(date).substring(0, 7);
        if (fetchedMonths.current.includes(monthDate)) {
            return;
        }

        const month = await LatestResultsApi.getDrawsForCalendar(lotteryslug, monthDate);
        setAllDraws((prevAllDraws) => mergeDraws(prevAllDraws, month.draws));
        fetchedMonths.current.push(monthDate);
    };

    const getResultsForLottery = async () => {
        setLoading(true);
        try {
            const results = await LatestResultsApi.getResultsForLottery(lotteryslug, selectedDrawNo);
            if (!results.lotteryDraw) {
                setNoDrawsYet(true);
                return;
            }
            setLotteryType(results.lotteryType);
            setLotteryDraw(results.lotteryDraw);
            setPrizes(results.prizes);
            setNumberPadding(results.ticketNumberPadding);

            const localDate = parseDateWithTimezone(results.lotteryDraw.date);
            setSelectedDate(localDate);
            setDailyDraws(
                results.drawsFromTheSameDay.map((draw) => ({
                    draw_no: draw.draw_no,
                    date: formatters.formatHour(draw.date, timezone),
                }))
            );

            if (!allDraws.length) {
                getDrawsForCalendar(localDate);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getResultsForLottery();
    }, [selectedDrawNo]);

    useEffect(() => {
        if (isDefault() && !defaultDate && drawDates.length) {
            const nearest = findNearestDate(new Date().toUTCString(), drawDates);
            setDefaultDate(nearest);
        }
    }, [defaultDate, drawDates]);

    useEffect(() => {
        if (isDefault() && !time && dailyDraws.length > 0) {
            setTime(dailyDraws[dailyDraws.length - 1].date);
        }
    }, [time, dailyDraws]);

    const findDrawByDate = (date: Date) => {
        const dateFormatted = formatDateToYMD(date);

        return allDraws.find((draw) => draw.date === dateFormatted);
    };

    useEffect(() => {
        if (isDefault()) {
            const parsedDate = new Date(defaultDate);
            const draw = findDrawByDate(parsedDate);

            if (draw) {
                setSelectedDrawNo(draw.draw_no);
                setNoDrawsForSelectedDate(false);
            } else {
                setNoDrawsForSelectedDate(true);
            }
        }
    }, [defaultDate]);

    useEffect(() => {
        if (isDefault() && time && dailyDraws.length > 0) {
            const matchingDraw = dailyDraws.find((draw) => draw.date === time);
            if (matchingDraw) {
                setSelectedDrawNo(matchingDraw.draw_no);
            }
        }
    }, [time]);

    const handleDayChange = (date: Date) => {
        const draw = findDrawByDate(date);
        if (draw) {
            setSelectedDrawNo(draw.draw_no);
        }
    };

    const getPrizes = () => {
        if (isTemplateThankYouLotto) {
            return null;
        }

        if (prizes.length < 1) {
            return null;
        }

        if (lotteryType === LotteryTypes.RaffleClosed) {
            return <RafflePrizes prizes={prizes} numberPadding={numberPadding} />;
        }

        return <Prizes lotterySlug={lotteryslug} prizes={prizes} />;
    };

    function parseDateWithTimezone(date: string) {
        return new Date(
            new Date(date).toLocaleString('en', { timeZone: timezone || process.env.MIX_LOTTERY_TIMEZONE })
        );
    }
    function isJackpotBannerEnabled() {
        if (!isTemplateDelta()) {
            return true;
        }

        return !(lotteryslug.includes('nap') || lotteryslug.includes('chance'));
    }

    function getBallsSection() {
        if (lotteryType === LotteryTypes.RaffleClosed || lotteryType === LotteryTypes.RaffleOpen) {
            const ticketNumbers = ensureNumberArray(lotteryDraw.numbers[0]);

            return (
                <div className="row no-gutters raffle-winning-tickets-container">
                    {!isTemplateThankYouLotto && (
                        <div className="align-self-center mr-3">
                            {t('results.winningTicket', { count: ticketNumbers.length })}
                        </div>
                    )}
                    <div className={`col-md col-12${isTemplateThankYouLotto ? '' : ' row'}`}>
                        {ticketNumbers.map((number, index) => (
                            <span
                                key={`${index + 1}`}
                                className={isTemplateThankYouLotto ? 'lotto-ball bg-info' : 'raffle-winning-ticket'}
                            >
                                {formatNumber(number, lotteryType, numberPadding)}
                            </span>
                        ))}
                    </div>
                </div>
            );
        }

        return (
            <div className={isDefault() ? '' : 'row no-gutters'}>
                <div className={`col-md-12 col-12 order-md-0 order-1 ${lotteryslug} lottery-type-${lotteryType}`}>
                    <Balls
                        lotteryDraw={lotteryDraw}
                        lotterySlug={lotteryslug}
                        usePrimaryBall={[LotteryTypes.Pick, LotteryTypes.Keno].includes(lotteryType)}
                    />
                </div>
                {!!drawinganimation && !!mapLotterySlugToMachine(lotteryslug) && (
                    <DrawAnimation results={lotteryDraw} lotterySlug={lotteryslug} />
                )}
            </div>
        );
    }

    const getBetsSection = () => <BetsSection lotteryDrawId={lotteryDraw.id} />;

    const baseView = () => (
        <div className={`draw-results results-details ${lotteryType ?? ''}`}>
            <div className="row no-gutters justify-content-between">
                <ResultsDate lotteryDraw={lotteryDraw} timezone={timezone} />
                <ResultsDropdowns
                    selectedDate={selectedDate}
                    onDayChange={handleDayChange}
                    draws={dailyDraws}
                    selectedDrawNo={selectedDrawNo || lotteryDraw.draw_no}
                    onSelectDraw={setSelectedDrawNo}
                    isDrawAvailable={(date) => !!findDrawByDate(date)}
                    onMonthChange={getDrawsForCalendar}
                />
            </div>

            {lotteryType === LotteryTypes.Soccer ? getBetsSection() : getBallsSection()}
            {isJackpotBannerEnabled() && (
                    <Jackpot jackpot={lotteryDraw.jackpot} currency={lotteryDraw.currency_code} />
                )}

            {getPrizes()}

            <Portal id="blockchainInfo">
                <BlockchainInfo results={lotteryDraw} url={transparencyurl} />
            </Portal>
        </div>
    );

    const defaultView = () => (
        <div className={`container results-details ${lotteryType ?? ''}`}>
            <div className="card border-0">
                <div className="card-body p-0">
                    <div className="top-section position-relative">
                        <div className="row g-0 justify-content-center align-items-center">
                            <div className="d-flex flex-wrap justify-content-center gap-2 pickers">
                                <DatePicker
                                    value={defaultDate}
                                    onChange={(v) => setDefaultDate(v)}
                                    maxDate={moment().endOf('day').toDate()}
                                    highlightDates={drawDates.map((item) => moment(item).toDate())}
                                    onMonthChange={getDrawsForCalendar}
                                />
                                {dailyDraws?.length > 1 && (
                                    <Select
                                        className="time-select"
                                        value={time}
                                        onChange={(event) => setTime(event.currentTarget.value)}
                                    >
                                        {dailyDraws.map((drawTime) => (
                                            <Option key={drawTime.date} value={drawTime.date}>
                                                {drawTime.date}
                                            </Option>
                                        ))}
                                    </Select>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="mid-section position-relative">
                        <>
                            {noDrawsForSelectedDate && (
                                <p className="no-results text-center mb-0">{t('results.noResultsYet')}</p>
                            )}
                            {!noDrawsForSelectedDate &&
                                (lotteryType === LotteryTypes.Soccer ? getBetsSection() : getBallsSection())}
                        </>
                    </div>
                    <div className="bottom-section position-relative">
                        <div className="row g-0 align-items-center">
                            <div className="col g-0 d-flex flex-column justify-content-center align-items-center">
                                {isJackpotBannerEnabled() && (
                                    <Jackpot jackpot={lotteryDraw.jackpot} currency={lotteryDraw.currency_code} />
                                )}
                                {prizes.length > 0 && getPrizes()}
                                <a href={Route('play', { lottery: lotteryslug })} className="btn btn-primary w-auto">
                                    {t('lotteryList.play')} {lotteryDraw.lottery.name}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const getContent = () => {
        return lotteryDraw ? (
            <LoadThemeComponent defaultView={defaultView} baseView={baseView} />
        ) : (
            noDrawsYet && (
                <div className={`box red-box w-auto ${!isDefault() ? 'mr-5 mt-5' : ''}`}>
                    <p>
                        <Trans i18nKey="results.theFirstDrawWasntHeldYet">
                            The first draw wasn't held yet. <a href={Route('play.all')}>Play now</a> and become the
                            first winner.
                        </Trans>
                    </p>
                </div>
            )
        );
    };

    return <Loader isLoading={loading} elementToShow={getContent} />;
}

function formatDateToYMD(date: Date): string {
    return [date.getFullYear(), `0${date.getMonth() + 1}`.slice(-2), `0${date.getDate()}`.slice(-2)].join('-');
}

function mergeDraws(drawsA: BasicDrawRecord[], drawsB: BasicDrawRecord[]): BasicDrawRecord[] {
    const combined = [...drawsA, ...drawsB];
    const uniqueDrawsMap = new Map<number, BasicDrawRecord>();
    combined.forEach((draw) => uniqueDrawsMap.set(draw.draw_no, draw));

    return Array.from(uniqueDrawsMap.values());
}

export default ResultsDetails;
